'use client';

import { XMarkIcon } from '@heroicons/react/24/outline';
import { sendGAEvent } from '@next/third-parties/google';
import clsx from 'clsx';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import { useEffect, useId, useRef, useState } from 'react';

import useContact from '@/hooks/useContact';

import { Button } from './primitives';

const baseUrl = `${process.env.NEXT_PUBLIC_API_URL ?? ''}`;

const yesNoQuestions = [
  'Do you currently struggle with PA forms?',
  'Do PAs slow down your clinic workflow?',
  'Is your team overloaded with admin work?',
  'Do insurance denials affect your revenue?',
  'Do you wish for more automation in your PA process?',
];

const situationOptions = [
  'I am an entry-level specialist',
  'I am a clinic manager or executive',
  'I work for an insurance company',
  'Other',
];

const outcomeOptions = ['Faster approvals', 'Less paperwork', 'One board for every case', 'Zero errors and delays'];

const triedOptions = ['Manual tracking', 'Spreadsheets', 'Hiring extra staff', 'Other software'];

const solutionOptions = [
  'Automated form completion',
  'Instant approvals',
  'A unified dashboard',
  'An error-free workflow',
];

const STEP_TITLES = [
  'Join early access',
  'How prior authorization runs today',
  'Your role',
  'What matters most in the next 90 days',
  'What you have tried',
  'What would help most',
  'Anything else we should know',
  'Your practice check',
] as const;

const TOTAL = STEP_TITLES.length;
const INDUSTRY_AVERAGE = 3;

type Answers = {
  info: { name: string; email: string; phone: string; company: string };
  yesno: Record<string, boolean>;
  single: Record<string, string>;
  multi: Record<string, string[]>;
  textarea: Record<string, string>;
};

const emptyAnswers = (): Answers => ({
  info: { name: '', email: '', phone: '', company: '' },
  yesno: {},
  single: {},
  multi: {},
  textarea: {},
});

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const FIELD =
  'mt-1.5 block w-full rounded-xl border bg-frost-50 px-3.5 py-2.5 text-base text-ink-900 outline-none focus:border-beacon-500';

function Field({
  id,
  label,
  optional,
  error,
  ...rest
}: React.ComponentPropsWithoutRef<'input'> & { id: string; label: string; optional?: boolean; error?: string }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-ink-900">
        {label}
        {optional ? <span className="font-normal text-muted"> (optional)</span> : null}
      </label>
      <input
        id={id}
        {...rest}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={clsx(FIELD, error ? 'border-rose' : 'border-ink-900/20')}
      />
      {error ? (
        <p id={`${id}-error`} className="mt-1.5 text-sm text-rose">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function Choice({
  selected,
  onClick,
  children,
  role = 'radio',
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  role?: 'radio' | 'checkbox';
}) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={selected}
      onClick={onClick}
      className={clsx(
        'flex w-full cursor-pointer items-center justify-between gap-4 rounded-xl border px-4 py-3 text-left text-[0.95rem] transition-colors duration-150',
        selected
          ? 'border-beacon-500 bg-beacon-500 text-white'
          : 'border-ink-900/15 bg-frost-50 text-ink-900 hover:border-beacon-500',
      )}
    >
      <span>{children}</span>
      <span
        aria-hidden="true"
        className={clsx(
          'h-4 w-4 shrink-0 border',
          role === 'radio' ? 'rounded-full' : 'rounded-[3px]',
          selected ? 'border-white bg-white' : 'border-ink-900/30',
        )}
      />
    </button>
  );
}

/**
 * Native <dialog>: the browser provides the focus trap, Escape handling,
 * inert background and focus return to the opener.
 */
export function WaitlistDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [answers, setAnswers] = useState<Answers>(emptyAnswers);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const reduce = useReducedMotion();
  const ids = useId();

  const { sendContact, loading } = useContact(baseUrl);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
      const previous = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previous;
      };
    }
    if (!open && el.open) el.close();
  }, [open]);

  const score = Object.values(answers.yesno).filter(Boolean).length;

  const buildMessagePayload = () => {
    const yesNoValues = yesNoQuestions.map((q) => (answers.yesno[q] ? 'yes' : 'no')).join(',');
    const situation = answers.single['situation'] || '';
    const outcome = answers.single['outcome'] || '';
    const solution = answers.single['solution'] || '';
    const tried = (answers.multi['tried'] || []).join(',');
    const notes = answers.textarea['notes'] || '';
    return [yesNoValues, situation, outcome, solution, tried, notes].join(';');
  };

  const reset = () => {
    setStep(1);
    setDirection(1);
    setAnswers(emptyAnswers());
    setEmailError(null);
    setSubmitError(null);
  };

  const requestClose = () => {
    sendGAEvent({ event: 'close_clicked_step', value: step.toString() });
    reset();
    onClose();
  };

  const prev = () => {
    sendGAEvent({ event: 'back_clicked_step', value: step.toString() });
    setDirection(-1);
    setStep((s) => Math.max(1, s - 1));
  };

  const next = async () => {
    sendGAEvent({ event: 'next_clicked_step', value: step.toString() });
    if (step === 1) {
      if (!isValidEmail(answers.info.email)) {
        setEmailError('Enter a valid work email so we can reply.');
        return;
      }
      setEmailError(null);
    }
    if (step === 7) {
      setSubmitError(null);
      const phone = answers.info.phone || '000-000-0000';
      const payload = {
        app: 'PAA Waiting List',
        name: answers.info.name,
        email: answers.info.email,
        phone,
        message: `ph: ${phone}/n${buildMessagePayload()}`,
      };
      try {
        await sendContact(payload);
      } catch (error) {
        console.error('Contact submission failed:', error);
        setSubmitError('We could not send your answers. Check your connection and try again.');
        return;
      }
    }
    setDirection(1);
    setStep((s) => Math.min(TOTAL, s + 1));
  };

  const slide = {
    enter: (dir: number) => (reduce ? { opacity: 0 } : { x: dir > 0 ? 40 : -40, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => (reduce ? { opacity: 0 } : { x: dir > 0 ? -40 : 40, opacity: 0 }),
  };

  const canContinue =
    step !== 1 || (answers.info.name.trim().length > 0 && answers.info.email.trim().length > 0);

  return (
    <dialog
      ref={ref}
      className="sheet z-50"
      aria-labelledby={`${ids}-title`}
      onCancel={(e) => {
        e.preventDefault();
        requestClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) requestClose();
      }}
    >
      <div className="flex h-full w-full items-end justify-center sm:items-center sm:p-6">
        <div className="relative flex max-h-[92dvh] w-full max-w-[34rem] flex-col rounded-t-[1.5rem] bg-white text-ink-900 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)] sm:rounded-[1.5rem]">
          <div className="rule-ink flex items-center justify-between border-b px-6 pt-5 pb-4">
            <div>
              <p className="text-xs font-medium text-muted">
                Step {step} of {TOTAL}
              </p>
              <h2 id={`${ids}-title`} className="font-display mt-0.5 text-[1.35rem] leading-none font-bold tracking-[-0.02em]">
                {STEP_TITLES[step - 1]}
              </h2>
            </div>
            <button
              type="button"
              onClick={requestClose}
              aria-label="Close"
              className="-mr-2 inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full text-ink-900/70 transition-colors hover:bg-frost-100 hover:text-ink-900"
            >
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="flex gap-1 px-6 pt-4" aria-hidden="true">
            {STEP_TITLES.map((s, i) => (
              <span
                key={s}
                className={clsx(
                  'h-[3px] flex-1 rounded-full transition-colors duration-300',
                  i < step ? 'bg-beacon-500' : 'bg-ink-900/15',
                )}
              />
            ))}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
            <AnimatePresence custom={direction} initial={false} mode="wait">
              <m.div
                key={step}
                custom={direction}
                variants={slide}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                className="space-y-4"
              >
                {step === 1 ? (
                  <form
                    className="space-y-4"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void next();
                    }}
                  >
                    <p className="text-[0.95rem] text-muted">
                      Seven short questions. They help us prepare for a conversation with your program.
                    </p>
                    <Field
                      id={`${ids}-name`}
                      label="Name"
                      autoComplete="name"
                      required
                      value={answers.info.name}
                      onChange={(e) => setAnswers({ ...answers, info: { ...answers.info, name: e.target.value } })}
                      onFocus={() => sendGAEvent({ event: 'input_clicked', value: 'info_name' })}
                    />
                    <Field
                      id={`${ids}-email`}
                      label="Work email"
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      required
                      error={emailError ?? undefined}
                      value={answers.info.email}
                      onChange={(e) => {
                        setEmailError(null);
                        setAnswers({ ...answers, info: { ...answers.info, email: e.target.value } });
                      }}
                      onFocus={() => sendGAEvent({ event: 'input_clicked', value: 'info_email' })}
                    />
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field
                        id={`${ids}-phone`}
                        label="Phone"
                        optional
                        type="tel"
                        autoComplete="tel"
                        inputMode="tel"
                        value={answers.info.phone}
                        onChange={(e) => setAnswers({ ...answers, info: { ...answers.info, phone: e.target.value } })}
                        onFocus={() => sendGAEvent({ event: 'input_clicked', value: 'info_phone' })}
                      />
                      <Field
                        id={`${ids}-company`}
                        label="Clinic or organization"
                        optional
                        autoComplete="organization"
                        value={answers.info.company}
                        onChange={(e) => setAnswers({ ...answers, info: { ...answers.info, company: e.target.value } })}
                        onFocus={() => sendGAEvent({ event: 'input_clicked', value: 'info_company' })}
                      />
                    </div>
                    <button type="submit" className="sr-only" tabIndex={-1} aria-hidden="true">
                      Continue
                    </button>
                  </form>
                ) : null}

                {step === 2 ? (
                  <ul className="space-y-3">
                    {yesNoQuestions.map((q) => {
                      const answered = q in answers.yesno;
                      return (
                        <li key={q} className="rule-ink flex flex-col gap-3 border-b pb-3 sm:flex-row sm:items-center sm:justify-between">
                          <span id={`${ids}-${q}`} className="text-[0.95rem]">
                            {q}
                          </span>
                          <div role="radiogroup" aria-labelledby={`${ids}-${q}`} className="flex shrink-0 gap-2">
                            {(['Yes', 'No'] as const).map((opt) => {
                              const selected = answered && answers.yesno[q] === (opt === 'Yes');
                              return (
                                <button
                                  key={opt}
                                  type="button"
                                  role="radio"
                                  aria-checked={selected}
                                  onClick={() => {
                                    setAnswers((p) => ({ ...p, yesno: { ...p.yesno, [q]: opt === 'Yes' } }));
                                    sendGAEvent({ event: 'input_clicked_step2', value: `yesno_${q}_${opt}` });
                                  }}
                                  className={clsx(
                                    'min-h-10 min-w-[4.25rem] cursor-pointer rounded-full border px-4 text-sm font-medium transition-colors',
                                    selected
                                      ? 'border-beacon-500 bg-beacon-500 text-white'
                                      : 'border-ink-900/15 bg-frost-50 hover:border-beacon-500',
                                  )}
                                >
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}

                {step === 3 ? (
                  <div role="radiogroup" aria-label="Your role" className="space-y-2.5">
                    {situationOptions.map((opt) => (
                      <Choice
                        key={opt}
                        selected={answers.single['situation'] === opt}
                        onClick={() => {
                          setAnswers((p) => ({ ...p, single: { ...p.single, situation: opt } }));
                          sendGAEvent({ event: 'input_clicked_step3', value: `situation_${opt}` });
                        }}
                      >
                        {opt}
                      </Choice>
                    ))}
                  </div>
                ) : null}

                {step === 4 ? (
                  <div role="radiogroup" aria-label="Desired outcome" className="space-y-2.5">
                    {outcomeOptions.map((opt) => (
                      <Choice
                        key={opt}
                        selected={answers.single['outcome'] === opt}
                        onClick={() => {
                          setAnswers((p) => ({ ...p, single: { ...p.single, outcome: opt } }));
                          sendGAEvent({ event: 'input_clicked_step4', value: `outcome_${opt}` });
                        }}
                      >
                        {opt}
                      </Choice>
                    ))}
                  </div>
                ) : null}

                {step === 5 ? (
                  <div role="group" aria-label="What you have tried" className="space-y-2.5">
                    <p className="text-sm text-muted">Choose all that apply.</p>
                    {triedOptions.map((opt) => {
                      const selected = (answers.multi['tried'] || []).includes(opt);
                      return (
                        <Choice
                          key={opt}
                          role="checkbox"
                          selected={selected}
                          onClick={() => {
                            setAnswers((p) => {
                              const cur = p.multi['tried'] || [];
                              return {
                                ...p,
                                multi: { ...p.multi, tried: selected ? cur.filter((o) => o !== opt) : [...cur, opt] },
                              };
                            });
                            sendGAEvent({ event: 'input_clicked_step5', value: `tried_${opt}` });
                          }}
                        >
                          {opt}
                        </Choice>
                      );
                    })}
                  </div>
                ) : null}

                {step === 6 ? (
                  <div role="radiogroup" aria-label="What would help most" className="space-y-2.5">
                    {solutionOptions.map((opt) => (
                      <Choice
                        key={opt}
                        selected={answers.single['solution'] === opt}
                        onClick={() => {
                          setAnswers((p) => ({ ...p, single: { ...p.single, solution: opt } }));
                          sendGAEvent({ event: 'input_clicked_step6', value: `solution_${opt}` });
                        }}
                      >
                        {opt}
                      </Choice>
                    ))}
                  </div>
                ) : null}

                {step === 7 ? (
                  <div>
                    <label htmlFor={`${ids}-notes`} className="block text-sm font-medium">
                      Notes for the team
                      <span className="font-normal text-muted"> (optional)</span>
                    </label>
                    <textarea
                      id={`${ids}-notes`}
                      rows={5}
                      placeholder="Payers you fight most, volumes, what a good first month would look like."
                      value={answers.textarea['notes'] || ''}
                      onChange={(e) => setAnswers((p) => ({ ...p, textarea: { ...p.textarea, notes: e.target.value } }))}
                      onFocus={() => sendGAEvent({ event: 'input_clicked_step7', value: 'additional_notes' })}
                      className={clsx(FIELD, 'border-ink-900/20')}
                    />
                    {submitError ? (
                      <p role="alert" className="mt-3 text-sm text-rose">
                        {submitError}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {step === 8 ? (
                  <div>
                    <p className="text-[1.05rem] leading-relaxed">
                      Thank you, {answers.info.name.trim().split(' ')[0] || 'there'}. Your answers are in and we will
                      reply within two business days.
                    </p>
                    <div className="rule-ink mt-6 border-t pt-5">
                      <p className="text-sm text-muted">Practice check</p>
                      <p className="font-display font-condensed mt-1 text-[2.75rem] leading-none font-extrabold tracking-[-0.02em]">
                        {score}
                        <span className="text-[0.5em] text-muted"> of {yesNoQuestions.length}</span>
                      </p>
                      <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-ink-900/10" aria-hidden="true">
                        <m.div
                          initial={reduce ? false : { width: 0 }}
                          animate={{ width: `${(score / yesNoQuestions.length) * 100}%` }}
                          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
                          className="h-full rounded-full bg-beacon-500"
                        />
                      </div>
                      <p className="mt-3 text-sm text-muted">
                        You said yes to {score} of {yesNoQuestions.length} pressure points. Programs we talk to average{' '}
                        {INDUSTRY_AVERAGE}.{' '}
                        {score >= INDUSTRY_AVERAGE ? 'There is a lot we can take off your plate.' : 'Your process is already ahead of most.'}
                      </p>
                    </div>
                  </div>
                ) : null}
              </m.div>
            </AnimatePresence>
          </div>

          <div className="rule-ink flex items-center justify-between gap-3 border-t px-6 py-4">
            {step > 1 && step < TOTAL ? (
              <Button variant="quiet-ink" className="min-h-11 px-4" onClick={prev}>
                Back
              </Button>
            ) : (
              <span />
            )}
            {step < TOTAL ? (
              <Button variant="beacon" className="min-h-11" onClick={() => void next()} disabled={loading || !canContinue}>
                {loading ? 'Sending' : step === 7 ? 'Send answers' : 'Continue'}
              </Button>
            ) : (
              <Button variant="ink" className="min-h-11" onClick={requestClose}>
                Done
              </Button>
            )}
          </div>
        </div>
      </div>
    </dialog>
  );
}
